import { RATE_REVIEW_CONSTANTS } from "../constants/rateReviewConstants";

/**
 * Rate Review Helper Functions
 * @module RateReviewHelpers
 */

/**
 * Returns a CSS class string based on the rate review lead status
 * @param {string} status - Lead status string
 * @returns {string} CSS class for status badge
 */
export const getStatusClass = (status) => {
  if (!status || typeof status !== "string") {
    return RATE_REVIEW_CONSTANTS.DEFAULT_STATUS_CLASS;
  }
  return (
    RATE_REVIEW_CONSTANTS.STATUS_CLASSES[status] ||
    RATE_REVIEW_CONSTANTS.DEFAULT_STATUS_CLASS
  );
};

/**
 * Extracts field value from lead object with fallback options
 * @private
 * @param {Object} lead - Lead object
 * @param {string[]} fieldNames - Array of possible field names to check
 * @param {any} defaultValue - Default value if field not found
 * @returns {any} Field value or default
 */
const extractFieldValue = (lead, fieldNames, defaultValue = "") => {
  for (const fieldName of fieldNames) {
    if (lead[fieldName] !== undefined && lead[fieldName] !== null) {
      return lead[fieldName];
    }
  }
  return defaultValue;
};

/**
 * Formats raw rate review lead data into a clean object for UI consumption
 * @param {Object} lead - Raw lead object from API
 * @returns {Object} Formatted rate review lead data with normalized field names
 */
export const formatRateReviewLead = (lead) => {
  if (!lead || typeof lead !== "object") {
    return {};
  }

  const gatewayId = extractFieldValue(
    lead,
    ["Gateway_ID", "gatewayId", "GatewayId", "Gateway"],
    ""
  );

  const salesAgent = extractFieldValue(
    lead,
    ["Sales_Agent", "salesRep"],
    ""
  );

  const salesAgentEmail = extractFieldValue(
    lead,
    ["Sales_Agent_Email", "salesRepEmail"],
    ""
  );

  const countryOfFormation = Array.isArray(lead.Country_of_Formation)
    ? lead.Country_of_Formation[0] || ""
    : extractFieldValue(lead, ["Country_of_Formation", "country"], "");

  const savings = extractFieldValue(lead, ["Savings", "savings"], "");

  const firstName = lead.First_Name || "";
  const lastName = lead.Last_Name || "";
  const contactName =
    `${firstName} ${lastName}`.trim() || "N/A";

  const createdTime = lead.Created_Time || "";
  const dateCreated = createdTime ? createdTime.split("T")[0] : "";

  const lastActivityTime = lead.Last_Activity_Time || lead.Modified_Time || "";
  const dateUpdated = lastActivityTime ? lastActivityTime.split("T")[0] : "";

  const leadStatus = lead.Lead_Status || RATE_REVIEW_CONSTANTS.DEFAULT_LEAD_STATUS;

  return {
    id: lead.id || lead.Id || "",
    businessName: lead.Company || "",
    Company: lead.Company || "",
    contactName,
    First_Name: firstName,
    Last_Name: lastName,
    email: lead.Email || "",
    Email: lead.Email || "",
    phone: lead.Phone || "",
    Phone: lead.Phone || "",
    gatewayId,
    Gateway_ID: gatewayId,
    Sales_Agent: salesAgent,
    salesRep: salesAgent,
    Sales_Agent_Email: salesAgentEmail,
    salesRepEmail: salesAgentEmail,
    Country_of_Formation: countryOfFormation,
    country: countryOfFormation,
    dateCreated,
    Created_Time: createdTime,
    dateUpdated,
    Last_Activity_Time: lastActivityTime,
    status: leadStatus,
    Lead_Status: leadStatus,
    statusClass: getStatusClass(leadStatus),
    Notes1: lead.Notes1 || "",
    savings,
    Lead_Source: lead.Lead_Source || RATE_REVIEW_CONSTANTS.LEAD_SOURCE,
  };
};

/**
 * Formats a byte count into a human-readable string (e.g. "1.25 MB")
 * @param {number|string} bytes - Byte count to format
 * @returns {string|null} Formatted string or null if invalid input
 */
export const formatBytes = (bytes) => {
  const value = Number(bytes);
  
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  const base = 1024;
  
  const unitIndex = Math.min(
    Math.floor(Math.log(value) / Math.log(base)),
    units.length - 1
  );
  
  const sizeInUnit = value / Math.pow(base, unitIndex);
  const decimalPlaces = unitIndex === 0 ? 0 : 2;
  
  return `${sizeInUnit.toFixed(decimalPlaces)} ${units[unitIndex]}`;
};
